import React from 'react';
import { Box, Typography, Stack, useTheme, alpha } from '@mui/material';

interface TyvraLogoProps {
  variant?: 'navbar' | 'footer' | 'large';
  scrolled?: boolean;
  onClick?: () => void;
}

const TyvraLogo: React.FC<TyvraLogoProps> = ({ variant = 'navbar', scrolled = false, onClick }) => {
  const theme = useTheme();

  const getSizes = () => {
    switch (variant) {
      case 'large':
        return { fontSize: '3rem', lotusSize: 40, stemStroke: 3 };
      case 'footer':
        return { fontSize: '2rem', lotusSize: 32, stemStroke: 2.5 };
      case 'navbar':
      default:
        return { fontSize: '1.5rem', lotusSize: 24, stemStroke: 2 };
    }
  };

  const sizes = getSizes();
  const isNavbar = variant === 'navbar';
  const isFooter = variant === 'footer';

  const getTextColor = () => {
    if (isFooter) {
      return `linear-gradient(135deg, white 0%, ${alpha('#ffffff', 0.7)} 100%)`;
    }
    if (isNavbar && scrolled) {
      return `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`;
    }
    return `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`;
  };

  const getStemColor = () => {
    if (isFooter) {
      return alpha('#ffffff', 0.6);
    }
    if (isNavbar && scrolled) {
      return theme.palette.primary.main;
    }
    return theme.palette.primary.dark;
  };

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-block',
        cursor: onClick ? 'pointer' : 'default',
        paddingBottom: '12px', // Small space for stem
      }}
      onClick={onClick}
    >
      {/* TYVRA Text */}
      <Typography
        sx={{
          fontSize: sizes.fontSize,
          fontWeight: 900,
          background: getTextColor(),
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          letterSpacing: '0.02em',
          position: 'relative',
          zIndex: 1,
          transition: 'all 0.3s ease',
          display: 'inline-block',
        }}
      >
        TYVRA
      </Typography>

      {/* Stem - Curved line that connects to lotus */}
      <svg
        width="100%"
        height="20"
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          overflow: 'visible',
          pointerEvents: 'none',
        }}
        viewBox="0 0 100 20"
        preserveAspectRatio="none"
      >
        {/* Curved stem that flows up to lotus position */}
        <path
          d="M 8 12 Q 50 8, 85 6"
          stroke={getStemColor()}
          strokeWidth={sizes.stemStroke}
          fill="none"
          strokeLinecap="round"
          style={{
            transition: 'all 0.3s ease',
          }}
        />
      </svg>

      {/* Lotus Flower (positioned at end of stem) */}
      <Box
        sx={{
          position: 'absolute',
          right: -sizes.lotusSize * 0.7,
          top: '50%',
          transform: 'translateY(-50%)',
          fontSize: `${sizes.lotusSize}px`,
          transition: 'all 0.3s ease',
          zIndex: 2,
          '&:hover': {
            transform: 'translateY(-50%) scale(1.2) rotate(5deg)',
          },
        }}
      >
        🪷
      </Box>
    </Box>
  );
};

export default TyvraLogo;

